/**
 * TileHeaderSlot - Renders controls in the tile-header location.
 *
 * This slot appears above each individual tile (in the title area) and is
 * typically used to show dimension labels like run name, step, or index.
 */

import React from 'react';

import {TileTitle} from '../../controls';
import {ControlDefinition, GridCell, isTileDimension} from '../types';

export type TileHeaderSlotProps = {
  controls: ControlDefinition[];
  cell: GridCell;
  disableRunLinks?: boolean;
};

/**
 * Renders controls in the tile-header location.
 *
 * Automatically determines which dimension to display based on slot configuration.
 * Only renders labels for non-empty cells.
 *
 * @param props - Tile header slot props
 * @returns Rendered tile header or null if hidden/empty
 */
export const TileHeaderSlot: React.FC<TileHeaderSlotProps> = ({
  controls,
  cell,
  disableRunLinks,
}) => {
  // Find the dimension control (run/step/index) that should be displayed as a label in this tile header.
  // We filter for 'label' type controls - these are dimensions shown as static text labels
  // rather than as interactive controls (sliders/tabs/dropdowns).
  const labelControl = controls.find(
    c => c.type === 'label' && isTileDimension(c.control)
  );

  // Don't render if cell is empty
  if (cell.isEmpty) {
    return null;
  }

  // We don't generally render the index label as a tile header, usually redundant
  if (labelControl && labelControl.control === 'index') {
    return null;
  }

  // For overlay-only tiles (masks without underlying image), render empty spacer
  // to maintain consistent spacing without showing text
  if (cell.maskOptions?.showImage === false) {
    return <div className="w-full min-w-0" style={{minHeight: '16px'}} />;
  }

  // If no label control found, still render a spacer to maintain alignment
  // This ensures mask/bounding box tiles align with regular media tiles
  if (!labelControl || !isTileDimension(labelControl.control)) {
    return <div className="w-full min-w-0" style={{minHeight: '16px'}} />;
  }

  return (
    <TileTitle
      dimension={labelControl.control}
      cell={cell}
      disableRunLinks={disableRunLinks}
    />
  );
};
